import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  Button,
  IconButton,
  CircularProgress,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
  Close as CloseIcon,
  Edit as EditIcon,
  Upload as UploadIcon,
  DeleteForever as DeleteForeverIcon,
  Warning as WarningIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import ManualHoldingsForm from "./ManualHoldingsForm";
import { useManualHoldings } from "./hooks/useManualHoldings";
import { useDeleteAllHoldings } from "./hooks/useDeleteAllHoldings";

interface AddHoldingsModalProps {
  open: boolean;
  onClose: () => void;
}

type ModalView = "method-selection" | "manual-entry" | "remove-confirmation";

const AddHoldingsModal: React.FC<AddHoldingsModalProps> = ({
  open,
  onClose,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [currentView, setCurrentView] = useState<ModalView>("method-selection");

  // Fetch manual holdings - only when modal is open and in manual-entry view
  const shouldFetchHoldings = open && currentView === "manual-entry";
  const { data: manualHoldingsData, isLoading: isLoadingManualHoldings } = useManualHoldings(shouldFetchHoldings);

  // Delete all holdings mutation
  const deleteAllHoldingsMutation = useDeleteAllHoldings();

  const handleClose = () => {
    // Reset to method selection when closing
    setCurrentView("method-selection");
    onClose();
  };

  const handleManualSuccess = () => {
    // Close modal after successful manual entry
    handleClose();
  };

  const handleCsvClick = () => {
    // Close modal and navigate to portfolio management page
    handleClose();
    navigate("/portfolio/manage");
  };

  const handleManualClick = () => {
    // Switch to manual entry view
    setCurrentView("manual-entry");
  };

  const handleRemoveAllClick = () => {
    // Switch to confirmation view
    setCurrentView("remove-confirmation");
  };

  const handleBackToSelection = () => {
    // Go back to method selection
    setCurrentView("method-selection");
  };

  const handleConfirmDelete = async () => {
    // Execute delete mutation
    try {
      await deleteAllHoldingsMutation.mutateAsync();
      // Close modal after successful deletion
      handleClose();
    } catch (error) {
      // Error is handled by mutation hook
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      slotProps={{
        paper: {
          sx: {
            backgroundColor:
              theme.palette.mode === "dark"
                ? "rgba(35, 35, 35, 0.98)"
                : "rgba(255, 255, 255, 0.98)",
            border:
              theme.palette.mode === "dark"
                ? "1px solid rgba(255, 255, 255, 0.08)"
                : "1px solid rgba(0, 0, 0, 0.06)",
            borderRadius: 3,
            maxHeight: "85vh",
          },
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          pb: 1,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          {currentView === "method-selection" && "Manage Holdings"}
          {currentView === "manual-entry" && "Add Holdings Manually"}
          {currentView === "remove-confirmation" && "Remove All Holdings"}
        </Typography>
        <IconButton onClick={handleClose} sx={{ color: "text.secondary" }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent
        sx={{
          pt: 2,
          display: "flex",
          flexDirection: "column",
          minHeight: currentView === "manual-entry" ? "500px" : "auto",
        }}
      >
        {currentView === "method-selection" ? (
          /* Method Selection View */
          <Box>
            <Typography
              variant="body1"
              color="text.secondary"
              sx={{ mb: 3 }}
            >
              Choose how you'd like to add your portfolio holdings:
            </Typography>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {/* Manual Entry Option */}
              <Box
                onClick={handleManualClick}
                sx={{
                  p: 3,
                  border:
                    theme.palette.mode === "dark"
                      ? "1px solid rgba(255, 255, 255, 0.08)"
                      : "1px solid rgba(0, 0, 0, 0.08)",
                  borderRadius: 3,
                  cursor: "pointer",
                  transition: "background-color 0.2s ease, border-color 0.2s ease",
                  backgroundColor:
                    theme.palette.mode === "dark"
                      ? "rgba(255, 255, 255, 0.02)"
                      : "rgba(0, 0, 0, 0.02)",
                  "&:hover": {
                    borderColor: "rgba(34, 197, 94, 0.5)",
                  },
                }}
              >
                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: 48,
                      height: 48,
                      borderRadius: 2,
                      backgroundColor: "rgba(34, 197, 94, 0.1)",
                      color: "#22c55e",
                      flexShrink: 0,
                    }}
                  >
                    <EditIcon fontSize="medium" />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      variant="h6"
                      sx={{ fontWeight: 600, mb: 0.5 }}
                    >
                      Manual Entry
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Manually enter your holdings by searching for companies and
                      adding the number of shares and average price you paid.
                    </Typography>
                    <Box sx={{ mt: 2 }}>
                      <Typography
                        variant="caption"
                        sx={{
                          color: "#22c55e",
                          fontWeight: 600,
                          textTransform: "uppercase",
                          letterSpacing: 0.5,
                        }}
                      >
                        Quick & Simple
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Box>

              {/* CSV Import Option */}
              <Box
                onClick={handleCsvClick}
                sx={{
                  p: 3,
                  border:
                    theme.palette.mode === "dark"
                      ? "1px solid rgba(255, 255, 255, 0.08)"
                      : "1px solid rgba(0, 0, 0, 0.08)",
                  borderRadius: 3,
                  cursor: "pointer",
                  transition: "background-color 0.2s ease, border-color 0.2s ease",
                  backgroundColor:
                    theme.palette.mode === "dark"
                      ? "rgba(255, 255, 255, 0.02)"
                      : "rgba(0, 0, 0, 0.02)",
                  "&:hover": {
                    borderColor: "rgba(59, 130, 246, 0.5)",
                  },
                }}
              >
                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: 48,
                      height: 48,
                      borderRadius: 2,
                      backgroundColor: "rgba(59, 130, 246, 0.1)",
                      color: "#3b82f6",
                      flexShrink: 0,
                    }}
                  >
                    <UploadIcon fontSize="medium" />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      variant="h6"
                      sx={{ fontWeight: 600, mb: 0.5 }}
                    >
                      Import from CSV
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Upload your complete trading history from supported brokers
                      (Trading212, Interactive Brokers, etc.) for accurate
                      portfolio tracking.
                    </Typography>
                    <Box sx={{ mt: 2 }}>
                      <Typography
                        variant="caption"
                        sx={{
                          color: "#3b82f6",
                          fontWeight: 600,
                          textTransform: "uppercase",
                          letterSpacing: 0.5,
                        }}
                      >
                        Full History
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Box>

              {/* Remove All Holdings Option */}
              <Box
                onClick={handleRemoveAllClick}
                sx={{
                  p: 3,
                  border:
                    theme.palette.mode === "dark"
                      ? "1px solid rgba(239, 68, 68, 0.2)"
                      : "1px solid rgba(239, 68, 68, 0.15)",
                  borderRadius: 3,
                  cursor: "pointer",
                  transition: "background-color 0.2s ease, border-color 0.2s ease",
                  backgroundColor:
                    theme.palette.mode === "dark"
                      ? "rgba(239, 68, 68, 0.04)"
                      : "rgba(239, 68, 68, 0.02)",
                  "&:hover": {
                    borderColor: "rgba(239, 68, 68, 0.5)",
                  },
                }}
              >
                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: 48,
                      height: 48,
                      borderRadius: 2,
                      backgroundColor: "rgba(239, 68, 68, 0.1)",
                      color: "#ef4444",
                      flexShrink: 0,
                    }}
                  >
                    <DeleteForeverIcon fontSize="medium" />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      variant="h6"
                      sx={{ fontWeight: 600, mb: 0.5 }}
                    >
                      Remove All Holdings
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Permanently delete all portfolio holdings (both manual and
                      CSV-imported) to start fresh. This cannot be undone.
                    </Typography>
                    <Box sx={{ mt: 2 }}>
                      <Typography
                        variant="caption"
                        sx={{
                          color: "#ef4444",
                          fontWeight: 600,
                          textTransform: "uppercase",
                          letterSpacing: 0.5,
                        }}
                      >
                        Start Fresh
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>
        ) : currentView === "remove-confirmation" ? (
          /* Remove Confirmation View */
          <Box>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                py: 4,
              }}
            >
              <Box
                sx={{
                  width: 80,
                  height: 80,
                  borderRadius: "50%",
                  backgroundColor: "rgba(239, 68, 68, 0.1)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mb: 3,
                }}
              >
                <WarningIcon
                  sx={{
                    fontSize: 48,
                    color: "#ef4444",
                  }}
                />
              </Box>

              <Typography
                variant="h5"
                sx={{
                  fontWeight: 700,
                  mb: 2,
                  color: "#ef4444",
                  textAlign: "center",
                }}
              >
                Warning: This Cannot Be Undone
              </Typography>

              <Typography
                variant="body1"
                color="text.secondary"
                sx={{
                  textAlign: "center",
                  mb: 1,
                  maxWidth: 500,
                }}
              >
                You are about to permanently delete{" "}
                <strong>all of your portfolio holdings</strong>, including:
              </Typography>

              <Box
                sx={{
                  mt: 2,
                  mb: 3,
                  p: 2,
                  backgroundColor:
                    theme.palette.mode === "dark"
                      ? "rgba(255, 255, 255, 0.02)"
                      : "rgba(0, 0, 0, 0.02)",
                  borderRadius: 2,
                  border:
                    theme.palette.mode === "dark"
                      ? "1px solid rgba(255, 255, 255, 0.1)"
                      : "1px solid rgba(0, 0, 0, 0.1)",
                }}
              >
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 1 }}
                >
                  • All manually added holdings
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 1 }}
                >
                  • All CSV-imported transaction history
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  • All calculated portfolio data and statistics
                </Typography>
              </Box>

              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  textAlign: "center",
                  fontWeight: 600,
                  mb: 4,
                }}
              >
                This action is irreversible. Your data will be permanently lost.
              </Typography>

              <Box
                sx={{
                  display: "flex",
                  gap: 2,
                  width: "100%",
                  justifyContent: "center",
                }}
              >
                <Button
                  onClick={handleBackToSelection}
                  variant="outlined"
                  disabled={deleteAllHoldingsMutation.isPending}
                  sx={{
                    minWidth: 140,
                    textTransform: "none",
                    fontWeight: 600,
                    borderRadius: 2,
                  }}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleConfirmDelete}
                  variant="contained"
                  disabled={deleteAllHoldingsMutation.isPending}
                  startIcon={
                    deleteAllHoldingsMutation.isPending ? (
                      <CircularProgress size={18} color="inherit" />
                    ) : (
                      <DeleteForeverIcon />
                    )
                  }
                  sx={{
                    minWidth: 180,
                    backgroundColor: "#ef4444",
                    color: "white",
                    fontWeight: 600,
                    textTransform: "none",
                    borderRadius: 2,
                    "&:hover": {
                      backgroundColor: "#dc2626",
                    },
                    "&:disabled": {
                      backgroundColor:
                        theme.palette.mode === "dark"
                          ? "rgba(255, 255, 255, 0.12)"
                          : "rgba(0, 0, 0, 0.12)",
                    },
                  }}
                >
                  {deleteAllHoldingsMutation.isPending
                    ? "Deleting..."
                    : "Delete All Holdings"}
                </Button>
              </Box>
            </Box>
          </Box>
        ) : (
          /* Manual Entry Form View */
          <Box sx={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
            <ManualHoldingsForm
              initialHoldings={manualHoldingsData?.holdings ?? []}
              isLoadingInitialData={isLoadingManualHoldings}
              onSuccess={handleManualSuccess}
              onCancel={handleBackToSelection}
            />
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default AddHoldingsModal;